import { CanvasUtils } from './CanvasUtils';

export class ClosetUtils {
    /**
     * Draw polygon edges and corner circles.
     * @param {CanvasRenderingContext2D} ctx
     * @param {Array} coordinates  Array of {x,y}
     * @param {string} strokeColor Colour of the polygon.
     */
    static drawPolygon(ctx, coordinates, strokeColor) {
        if (!coordinates || coordinates.length === 0) return;
        // Draw edges
        CanvasUtils.drawLines(ctx, coordinates, strokeColor);
        // Draw corner circles
        coordinates.forEach((coord) => {
            CanvasUtils.drawCircle(ctx, coord, 5, strokeColor);
        });
    }

    /**
     * Draw distance labels for each edge of a polygon.
     * @param {CanvasRenderingContext2D} ctx
     * @param {Array} coordinates  Array of {x,y}
     * @param {number} scaleFactor  Factor for converting px to inches/feet.
     * @param {Function} labelDrawer   Callback that draws label (mostly CanvasUtils.drawDistanceLabel or similar)
     */
    static drawEdgeLabels(ctx, coordinates, scaleFactor, labelDrawer) {
        if (!coordinates || coordinates.length === 0) return;
        coordinates.forEach((coord, index) => {
            labelDrawer(
                ctx,
                coordinates[(index + 1) % coordinates.length],
                coord,
                scaleFactor,
            );
        });
    }

    /**
     * Draw interactive midpoint circles with a plus icon overlay.
     * @param {CanvasRenderingContext2D} ctx
     * @param {Array} coordinates Array of polygon coordinates
     * @param {HTMLImageElement} iconImage plus icon image element (already loaded)
     */
    static drawMidPoints(ctx, coordinates, iconImage) {
        if (!coordinates || coordinates.length < 2) return;
        coordinates.forEach((coord, index) => {
            const nextIndex = (index + 1) % coordinates.length;
            const next = coordinates[nextIndex];
            const midPoint = {
                x: (coord.x + next.x) / 2,
                y: (coord.y + next.y) / 2,
            };
            const distPx = CanvasUtils.distanceInPixels(coord, next);
            const radius = Math.min(distPx * 0.1, 8);
            ctx.fillStyle = 'black';
            ctx.beginPath();
            ctx.arc(midPoint.x, midPoint.y, radius, 0, Math.PI * 2, true);
            ctx.fill();
            if (iconImage) {
                ctx.drawImage(
                    iconImage,
                    midPoint.x - radius,
                    midPoint.y - radius,
                    radius * 2,
                    radius * 2,
                );
            }
        });
    }

    /**
     * Return the smallest edge length (inches) for a polygon.
     */
    static minEdgeLengthInches(polygon, scaleFactor) {
        let minLen = Infinity;
        for (let i = 0; i < polygon.length; i++) {
            const p1 = polygon[i];
            const p2 = polygon[(i + 1) % polygon.length];
            const len = CanvasUtils.measureDistance(p1, p2, scaleFactor);
            minLen = Math.min(minLen, len);
        }
        return minLen;
    }

    /**
     * Validate that obstacle size does not exceed closet min edge.
     * Returns {valid:boolean, msg?:string}
     */
    static validateObstacleSize(obstacleType, dims, minEdge) {
        if (obstacleType === 'RECTANGLE') {
            const { width, height } = dims;
            if (minEdge < width * 0.9 || minEdge < height * 0.9) {
                return {
                    valid: false,
                    msg: 'Obstacle size cannot greater than closet',
                };
            }
        }
        if (obstacleType === 'CIRCLE') {
            const { radius } = dims;
            if (minEdge < radius * 0.9) {
                return {
                    valid: false,
                    msg: 'Obstacle size maybe greater than closet',
                };
            }
        }
        return { valid: true };
    }

    /**
     * Scale obstacle dimensions by factor & multiplier (default 1.5).
     */
    static scaledObstacleData(dims, scaleFactor, mul = 1.5) {
        return {
            width: dims.width ? dims.width * scaleFactor * mul : undefined,
            height: dims.height ? dims.height * scaleFactor * mul : undefined,
            radius: dims.radius ? dims.radius * scaleFactor * mul : undefined,
        };
    }
}
